// MessageAttachments — inline thumbnails for any images/PDFs/files attached to
// a message. Images show a small thumbnail; PDFs show a PDF-doc thumbnail; both
// open <MediaViewer> on tap (a full-screen image viewer / browser-native PDF
// viewer). Other files render a download/open chip. Renders nothing when the
// message has no attachments, so existing fixture-backed messages are unchanged.
//
// The message shape is genuinely variable across the RPC, the legacy table read,
// and the repaired transcripts (see lib/messageShape.js), so the attachment
// reader is TOLERANT — it accepts any common attachment key (attachments / files
// / media / images / attachments_url) and each item may be a string URL or an
// object under any common field (url/href/src/link/path, name/filename/title,
// content_type/mime/mime_type/type, thumb/thumbnail/thumb_url/thumbnail_url).
// INLINE MARKDOWN IMAGES: ClickUp/Slack messages embed uploaded images in the
// body as Markdown image syntax, ![alt](url), e.g.
//   ![Screenshot 2026-07-22 at 11.52.50 PM.png](https://…/Screenshot….png)
// There is NO separate attachments/files field on the message — the image URL
// lives only inside the body text. extractInlineImages() pulls these out so the
// body renders without the dead "!Screenshot….png" text and the images show as
// thumbnails below the message (reusing this component + <MediaViewer>).
import React, { useState } from "react";
import MediaViewer, { classifyAttachment } from "./MediaViewer";
import MarkdownReader from "./MarkdownReader";

function basename(url) {
  if (!url) return "";
  try {
    const path = new URL(url, "http://x").pathname;
    return decodeURIComponent(path.split("/").pop() || url);
  } catch {
    return String(url).split("/").pop() || url;
  }
}

// Markdown image syntax: ![alt](url). The URL carries no spaces/`)` (ClickUp
// encodes them), so [^\s)]+ bounds it. `g` flag — a body can carry several.
const INLINE_IMG_RE = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;

/**
 * Pull ![alt](url) images out of a message body. Returns the body with the image
 * markdown removed (so it doesn't render as dead "!filename.png" text) and an
 * images array shaped like readAttachments output for <MessageAttachments>.
 * @param {string} body
 * @returns {{ body: string, images: Array<{ url: string, name: string, type: string, thumb: null }> }}
 */
export function extractInlineImages(body) {
  if (!body || typeof body !== "string") return { body: body || "", images: [] };
  const images = [];
  const cleaned = body.replace(INLINE_IMG_RE, (_full, alt, url) => {
    images.push({ url, name: alt || basename(url), type: "", thumb: null });
    return ""; // remove the image markdown from the text flow
  });
  return { body: cleaned, images };
}

/**
 * Pull markdown-file links out of a message body. ClickUp attaches .md docs as
 * markdown links in the body, e.g.
 *   [ORG-MEMBERS-REMOVED-STATUS-ISSUES.md](https://…/ORG-MEMBERS-REMOVED-STATUS-ISSUES.md)
 * This extracts `[label](url)` where the label OR url ends in .md/.markdown (so
 * regular links to ClickUp tasks/zaps are left in the body for linkify), and
 * bare urls ending in .md. Returns the body with those removed and a files array
 * shaped like readAttachments output for <MessageAttachments>.
 * @param {string} body
 * @returns {{ body: string, files: Array<{ url: string, name: string, type: string, thumb: null }> }}
 */
export function extractMarkdownFiles(body) {
  if (!body || typeof body !== "string") return { body: body || "", files: [] };
  const files = [];
  // [label](url) where label OR url ends in .md/.markdown.
  const linkRe = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  const cleaned = body.replace(linkRe, (full, label, url) => {
    if (/\.(md|markdown)(\?|#|$)/i.test(label) || /\.(md|markdown)(\?|#|$)/i.test(url)) {
      files.push({ url, name: label || basename(url), type: "text/markdown", thumb: null });
      return "";
    }
    return full; // leave regular links in the body
  });
  // Bare url ending in .md/.markdown (no markdown wrapper).
  const bareRe = /(^|\s)(https?:\/\/[^\s<]+\.(?:md|markdown)(?:\?[^\s]*)?)/gi;
  const cleaned2 = cleaned.replace(bareRe, (_full, lead, url) => {
    files.push({ url, name: basename(url), type: "text/markdown", thumb: null });
    return lead;
  });
  return { body: cleaned2, files };
}

/**
 * Pull inline video links out of a message body. ClickUp attaches videos (.mov,
 * .mp4, …) as markdown links in the body, e.g.
 *   [visitor intelligence.mov](https://…/visitor%20intelligence.mov?view=open)
 * and sometimes as bare URLs. This extracts `[label](url)` where the label OR
 * url ends in a video extension (so regular links to ClickUp tasks/zaps stay in
 * the body for linkify) and bare urls ending in a video extension. Returns the
 * body with those removed and a videos array shaped like readAttachments output
 * for <MessageAttachments> (kind → "video").
 * @param {string} body
 * @returns {{ body: string, videos: Array<{ url: string, name: string, type: string, thumb: null }> }}
 */
export function extractInlineVideos(body) {
  if (!body || typeof body !== "string") return { body: body || "", videos: [] };
  const videos = [];
  const extRe = /\.(?:mov|mp4|webm|ogv|m4v)(?:\?|#|$)/i;
  // [label](url) where label OR url ends in a video extension.
  const linkRe = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  const cleaned = body.replace(linkRe, (full, label, url) => {
    if (extRe.test(label) || extRe.test(url)) {
      videos.push({ url, name: label || basename(url), type: "video/mp4", thumb: null });
      return "";
    }
    return full; // leave regular links in the body
  });
  // Bare url ending in a video extension (no markdown wrapper).
  const bareRe = /(^|\s)(https?:\/\/[^\s<]+\.(?:mov|mp4|webm|ogv|m4v)(?:\?[^\s]*)?)/gi;
  const cleaned2 = cleaned.replace(bareRe, (_full, lead, url) => {
    videos.push({ url, name: basename(url), type: "video/mp4", thumb: null });
    return lead;
  });
  return { body: cleaned2, videos };
}

/**
 * Read attachments off a message, tolerant of key-name variance. Normalizes to
 * an array of { url, name, type, thumb }. Empty when there are none.
 * @param {object} m
 * @returns {Array<{ url: string, name: string, type: string, thumb: string|null }>}
 */
export function readAttachments(m) {
  if (!m) return [];
  const raw =
    m.attachments ?? m.files ?? m.media ?? m.images ?? m.attachments_url ?? null;
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const out = [];
  for (const a of list) {
    if (!a) continue;
    if (typeof a === "string") {
      out.push({ url: a, name: basename(a), type: "", thumb: null });
      continue;
    }
    const url =
      a.url ?? a.href ?? a.src ?? a.link ?? a.path ?? a.location ?? null;
    if (!url) continue;
    const name = a.name ?? a.filename ?? a.title ?? a.label ?? basename(url);
    const type = a.content_type ?? a.mime ?? a.mime_type ?? a.type ?? "";
    const thumb =
      a.thumb ?? a.thumbnail ?? a.thumb_url ?? a.thumbnail_url ?? null;
    out.push({ url, name, type, thumb });
  }
  return out;
}

function PdfIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <text x="12" y="17" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="700">
        PDF
      </text>
    </svg>
  );
}

function FileIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function MdIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M7 15V9l3 3 3-3v6" />
      <path d="M17 9v6" />
      <path d="M15 13l2 2 2-2" />
    </svg>
  );
}

/** Play badge overlayed on a video thumbnail. */
function PlayBadge() {
  return (
    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white shadow-lg ring-1 ring-white/30">
        <svg className="ml-0.5 h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </span>
  );
}

/**
 * @param {object} props
 * @param {object} props.message  The message row; attachments are read tolerantly.
 */
export default function MessageAttachments({ message, inlineImages = [], markdownFiles = [], inlineVideos = [] }) {
  const items = [...readAttachments(message), ...inlineImages, ...markdownFiles, ...inlineVideos];
  const [viewing, setViewing] = useState(null);
  const [reading, setReading] = useState(null);

  if (items.length === 0) return null;

  return (
    <div className="mt-2" data-testid="message-attachments">
      <div className="flex flex-wrap gap-2">
        {items.map((att, i) => {
          const kind = classifyAttachment(att.type, att.url);
          if (kind === "image") {
            return (
              <button
                type="button"
                key={`att-${i}`}
                onClick={() => setViewing(att)}
                className="group relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                aria-label={`Open image: ${att.name}`}
                data-testid="attachment-image"
              >
                <img
                  src={att.thumb || att.url}
                  alt={att.name}
                  loading="lazy"
                  className="h-20 w-20 object-cover transition group-hover:opacity-90"
                />
              </button>
            );
          }
          if (kind === "pdf") {
            return (
              <button
                type="button"
                key={`att-${i}`}
                onClick={() => setViewing(att)}
                className="group flex w-44 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-left hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                aria-label={`Open PDF: ${att.name}`}
                data-testid="attachment-pdf"
              >
                <PdfIcon className="h-8 w-8 shrink-0 text-red-500 dark:text-red-400" />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                    {att.name}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    PDF
                  </span>
                </span>
              </button>
            );
          }
          if (kind === "markdown") {
            return (
              <button
                type="button"
                key={`att-${i}`}
                onClick={() => setReading(att)}
                className="group flex w-44 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-left hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                aria-label={`Open document: ${att.name}`}
                data-testid="attachment-md"
              >
                <MdIcon className="h-8 w-8 shrink-0 text-blue-500 dark:text-blue-400" />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                    {att.name}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Markdown
                  </span>
                </span>
              </button>
            );
          }
          if (kind === "video") {
            return (
              <button
                type="button"
                key={`att-${i}`}
                onClick={() => setViewing(att)}
                className="group relative flex w-44 flex-col items-center gap-1 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-center hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                aria-label={`Play video: ${att.name}`}
                data-testid="attachment-video"
              >
                <span className="relative flex h-20 w-full items-center justify-center overflow-hidden rounded bg-gray-900 dark:bg-black">
                  {att.thumb ? (
                    <img
                      src={att.thumb}
                      alt={att.name}
                      loading="lazy"
                      className="h-full w-full object-cover opacity-90"
                    />
                  ) : (
                    <video
                      src={att.url}
                      preload="metadata"
                      muted
                      className="h-full w-full object-cover opacity-90"
                    />
                  )}
                  <PlayBadge />
                </span>
                <span className="min-w-0 w-full">
                  <span className="block truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                    {att.name}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Video
                  </span>
                </span>
              </button>
            );
          }
          return (
            <a
              key={`att-${i}`}
              href={att.url}
              target="_blank"
              rel="noreferrer"
              className="group flex w-44 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600 dark:hover:bg-gray-800"
              data-testid="attachment-file"
            >
              <FileIcon className="h-8 w-8 shrink-0 text-gray-400 dark:text-gray-500" />
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                  {att.name}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  File
                </span>
              </span>
            </a>
          );
        })}
      </div>

      {viewing && (
        <MediaViewer attachment={viewing} onClose={() => setViewing(null)} />
      )}
      {reading && (
        <MarkdownReader attachment={reading} onClose={() => setReading(null)} />
      )}
    </div>
  );
}